import React, { useState, useRef, useMemo } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, Eye, EyeOff, Sun, Contrast, RotateCcw, Trash2, Sparkles, Check, X } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import Modal from '../components/Modal'
import { CONDITION_STYLE, hexFor, labelFor } from '../lib/conditionColors'

// FDI two-digit notation (matches Odontogram.jsx / the backend tooth vocabulary).
const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28]
const LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]
const ALL_TEETH = [...UPPER, ...LOWER]

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : ''

// Phase 2A — co-diagnosis markup. Dentist places findings ON a 2D scan image; each becomes a
// FindingAnnotation and (optionally) a dental-chart entry. Reads the SAME image the SIDEXIS viewer
// uses (/imaging/:id/dicom, PNG). No money code. (Paul 2026-07-04.)
export default function ScanMarkup({ patient, study, annotations = [], conditions = [], condition_suggestions = {} }) {
  const [patientView, setPatientView] = useState(false)
  const [invert, setInvert] = useState(false)
  const [brightness, setBrightness] = useState(1)
  const [contrast, setContrast] = useState(1)
  const IMG_SOURCES = [
    `/imaging/${study.id}/orthanc_preview`, // Orthanc-rendered preview (server-side creds); 404s → falls through
    `/imaging/${study.id}/dicom`,        // diagnostic DICOM render (real X-rays, via the render service)
    `/imaging/${study.id}/image?full=1`, // full snapshot if one was generated
    `/imaging/${study.id}/image`,        // preview.jpg — present for essentially every indexed study/photo
  ]
  const [srcIdx, setSrcIdx] = useState(0)
  const imgSrc = IMG_SOURCES[Math.min(srcIdx, IMG_SOURCES.length - 1)]

  // Placement modal state
  const [pending, setPending] = useState(null)      // {x, y} normalised — a new mark being placed
  const [condition, setCondition] = useState('caries')
  const [tooth, setTooth] = useState('')
  const [note, setNote] = useState('')
  const [chartIt, setChartIt] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const [runningAI, setRunningAI] = useState(false)

  const wrapRef = useRef(null)
  const suggestion = condition ? condition_suggestions[condition] : null

  // Confirmed marks (manual, or AI suggestions a dentist Accepted) vs. AI proposals awaiting review.
  const confirmedMarks = useMemo(() => annotations.filter((a) => a.status !== 'suggested'), [annotations])
  const suggestedMarks = useMemo(() => annotations.filter((a) => a.status === 'suggested'), [annotations])
  // Findings LIST: patient view shows only confirmed + patient-visible; dentist sees all confirmed.
  const listMarks = useMemo(
    () => (patientView ? confirmedMarks.filter((a) => a.patient_visible) : confirmedMarks),
    [confirmedMarks, patientView]
  )
  // Marks drawn ON the image: dentist also sees pending AI suggestions (styled distinctly); a patient
  // never sees an unconfirmed suggestion. Only marks with a real 2D point are drawn — CBCT anatomy
  // findings have no honest 2D location (geometry {}), so they appear in the review LIST only.
  const hasPoint = (a) => typeof a.geometry?.x === 'number' && typeof a.geometry?.y === 'number'
  const overlayMarks = useMemo(
    () => (patientView ? confirmedMarks.filter((a) => a.patient_visible) : annotations).filter(hasPoint),
    [annotations, confirmedMarks, patientView]
  )

  const onImageError = () => {
    // Walk the fallback chain (DICOM → snapshot → preview.jpg) so a scan always renders to mark on.
    setSrcIdx((i) => (i < IMG_SOURCES.length - 1 ? i + 1 : i))
  }

  const placeMark = (e) => {
    if (patientView) return
    const rect = wrapRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    if (x < 0 || x > 1 || y < 0 || y > 1) return
    setPending({ x: Number(x.toFixed(4)), y: Number(y.toFixed(4)) })
    setCondition('caries'); setTooth(''); setNote(''); setChartIt(true)
  }

  const save = () => {
    if (!pending) return
    setSaving(true)
    router.post(`/patients/${patient.id}/finding_annotations`, {
      imaging_study_id: study.id,
      tooth_number: tooth || null,
      condition,
      shape: 'point',
      x: pending.x, y: pending.y,
      note: note || null,
      chart_it: chartIt,
      procedure_code_id: (chartIt && suggestion) ? suggestion.id : null,
      patient_visible: true,
    }, {
      preserveScroll: true,
      onSuccess: (page) => { toast.success(page?.props?.flash?.notice || 'Finding marked'); setPending(null) },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not mark finding'),
      onFinish: () => setSaving(false),
    })
  }

  const remove = (id) => {
    router.delete(`/finding_annotations/${id}`, {
      preserveScroll: true,
      onSuccess: () => { toast.success('Finding removed'); setConfirmDelete(null) },
      onError: () => toast.error('Could not remove finding'),
    })
  }

  // Phase 3 — decision-support. Ask the AI to analyse this scan; findings return as "suggested" marks
  // the dentist then Accepts/Rejects. Nothing is charted or billed automatically.
  const runAI = () => {
    setRunningAI(true)
    router.post(`/patients/${patient.id}/imaging/${study.id}/analyze`, {}, {
      preserveScroll: true,
      onSuccess: (page) => toast.success(page?.props?.flash?.notice || 'AI analysis started'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not start AI analysis'),
      onFinish: () => setRunningAI(false),
    })
  }

  // A suggestion is chartable if it names a tooth and a condition that maps to work (not healthy/missing).
  const chartableMark = (m) => !!m.tooth_number && m.condition !== 'healthy' && m.condition !== 'missing'

  const acceptSuggestion = (m) => {
    router.post(`/finding_annotations/${m.id}/accept`, { chart_it: chartableMark(m) }, {
      preserveScroll: true,
      onSuccess: (page) => toast.success(page?.props?.flash?.notice || 'AI finding accepted'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not accept finding'),
    })
  }

  const rejectSuggestion = (m) => {
    router.post(`/finding_annotations/${m.id}/reject`, {}, {
      preserveScroll: true,
      onSuccess: () => toast.success('AI suggestion dismissed'),
      onError: () => toast.error('Could not dismiss suggestion'),
    })
  }

  const pct = (c) => (c || c === 0 ? `${Math.round(c * 100)}%` : null)

  const legendConditions = [...new Set(listMarks.map((m) => m.condition))]

  return (
    <DashboardLayout>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link href={`/patients/${patient.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-primary">
            <ArrowLeft size={15} /> Back to {patient.first_name || patient.name}
          </Link>
          <h1 className="mt-1 text-lg font-semibold text-brand-ink">
            Mark findings — {study.modality_label}{study.captured_at ? ` · ${fmtDate(study.captured_at)}` : ''}
          </h1>
          <p className="text-sm text-brand-muted">
            {patientView ? 'Patient view — notes hidden.' : 'Click the image to place a finding. Tick “Also chart it” to add it to the dental chart.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!patientView && (
            <button
              type="button"
              onClick={runAI}
              disabled={runningAI}
              title="AI decision-support — proposes findings for you to review, accept or reject. Never charts or bills automatically."
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary/40 bg-brand-primary/10 px-3 py-2 text-sm font-semibold text-brand-primary transition hover:bg-brand-primary/15 disabled:opacity-50"
            >
              <Sparkles size={15} /> {runningAI ? 'Analysing…' : 'Run AI analysis'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setPatientView((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-white px-3 py-2 text-sm font-semibold text-brand-ink transition hover:bg-brand-surface/50"
          >
            {patientView ? <><Eye size={15} /> Dentist view</> : <><EyeOff size={15} /> Patient view</>}
          </button>
        </div>
      </div>

      {/* Image controls */}
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-brand-border bg-white px-4 py-2 text-xs text-brand-muted">
        <label className="flex items-center gap-2"><Sun size={14} /> Brightness
          <input type="range" min="0.3" max="2" step="0.05" value={brightness} onChange={(e) => setBrightness(parseFloat(e.target.value))} className="w-28 accent-brand-primary" />
        </label>
        <label className="flex items-center gap-2"><Contrast size={14} /> Contrast
          <input type="range" min="0.5" max="3" step="0.05" value={contrast} onChange={(e) => setContrast(parseFloat(e.target.value))} className="w-28 accent-brand-primary" />
        </label>
        <button type="button" onClick={() => setInvert((v) => !v)} className={`rounded px-2 py-1 ${invert ? 'bg-brand-ink text-white' : 'hover:bg-brand-surface/60'}`}>Invert</button>
        <button type="button" onClick={() => { setInvert(false); setBrightness(1); setContrast(1) }} className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-brand-surface/60"><RotateCcw size={13} /> Reset</button>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Image + overlay */}
        <div className="flex-1">
          <div
            ref={wrapRef}
            onClick={placeMark}
            className={`relative inline-block max-w-full select-none overflow-hidden rounded-lg border border-brand-border bg-black ${patientView ? '' : 'cursor-crosshair'}`}
          >
            <img
              src={imgSrc}
              alt={study.modality_label}
              draggable={false}
              onError={onImageError}
              className="block max-h-[72vh] max-w-full object-contain"
              style={{ filter: `invert(${invert ? 1 : 0}) brightness(${brightness}) contrast(${contrast})` }}
            />
            <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {overlayMarks.map((m) => {
                const gx = (m.geometry?.x ?? 0) * 100
                const gy = (m.geometry?.y ?? 0) * 100
                const isSuggested = m.status === 'suggested'
                // AI proposals are drawn amber + dashed so they read as "pending review", not confirmed.
                const hex = isSuggested ? '#d97706' : hexFor(m.condition)
                return (
                  <g key={m.id}>
                    <circle
                      cx={gx} cy={gy} r="1.8" fill={hex} fillOpacity={isSuggested ? 0.12 : 0.35}
                      stroke={hex} strokeWidth={isSuggested ? 0.7 : 0.6}
                      strokeDasharray={isSuggested ? '1.2 0.8' : undefined}
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle cx={gx} cy={gy} r="0.5" fill={hex} vectorEffect="non-scaling-stroke" />
                  </g>
                )
              })}
            </svg>
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-2">
            {legendConditions.length === 0 && (
              <span className="text-xs text-brand-muted">No findings marked yet{patientView ? '' : ' — click the image to add one'}.</span>
            )}
            {legendConditions.map((c) => (
              <span key={c} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium ${CONDITION_STYLE[c] || CONDITION_STYLE.healthy}`}>
                {labelFor(c)}
              </span>
            ))}
          </div>
        </div>

        {/* Findings list */}
        <div className="w-full lg:w-80">
          {/* AI suggestions awaiting review (dentist view only) — decision-support, not confirmed. */}
          {!patientView && suggestedMarks.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50/60 p-3">
              <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-amber-800">
                <Sparkles size={14} /> AI-suggested — review ({suggestedMarks.length})
              </h2>
              <p className="mb-2 text-[11px] text-amber-700">
                Decision-support only. Nothing is charted or billed until you Accept it.
              </p>
              <ul className="space-y-2">
                {suggestedMarks.map((m) => (
                  <li key={m.id} className="rounded-md border border-amber-300 bg-white p-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-2 font-medium text-brand-ink">
                        <span className="h-2.5 w-2.5 rounded-full ring-2 ring-amber-300" style={{ background: '#d97706' }} />
                        {m.tooth_number ? `Tooth ${m.tooth_number} — ` : ''}{labelFor(m.condition)}
                      </span>
                      {pct(m.confidence) && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">{pct(m.confidence)}</span>
                      )}
                    </div>
                    {m.model_tag && <div className="mt-0.5 text-[10px] text-brand-muted">model: {m.model_tag}</div>}
                    {m.note && <div className="mt-1 text-xs text-brand-muted">{m.note}</div>}
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => acceptSuggestion(m)}
                        className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-emerald-600 px-2 py-1.5 text-xs font-semibold text-white transition hover:opacity-90">
                        <Check size={13} /> Accept{chartableMark(m) ? ' + chart' : ''}
                      </button>
                      <button type="button" onClick={() => rejectSuggestion(m)}
                        className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-brand-border bg-white px-2 py-1.5 text-xs font-semibold text-brand-muted transition hover:bg-red-50 hover:text-red-600">
                        <X size={13} /> Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <h2 className="mb-2 text-sm font-semibold text-brand-ink">Findings ({listMarks.length})</h2>
          {listMarks.length === 0 ? (
            <p className="text-sm text-brand-muted">Nothing marked on this scan yet.</p>
          ) : (
            <ul className="space-y-2">
              {listMarks.map((m) => (
                <li key={m.id} className="rounded-lg border border-brand-border bg-white p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-2 font-medium text-brand-ink">
                      <span className="h-3 w-3 rounded-full" style={{ background: hexFor(m.condition) }} />
                      {m.tooth_number ? `Tooth ${m.tooth_number} — ` : ''}{labelFor(m.condition)}
                      {m.source === 'ai' && (
                        <span className="rounded bg-brand-primary/10 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand-primary" title="Accepted from an AI suggestion">AI</span>
                      )}
                    </span>
                    {!patientView && (
                      <button type="button" onClick={() => setConfirmDelete(m)} className="rounded p-1 text-brand-muted hover:bg-red-50 hover:text-red-600" title="Remove finding">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {m.charted && <div className="mt-1 text-[11px] font-medium text-emerald-700">On the dental chart</div>}
                  {!patientView && m.note && <div className="mt-1 text-xs text-brand-muted">{m.note}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Placement modal */}
      <Modal
        open={!!pending}
        onClose={() => setPending(null)}
        title="Mark a finding"
        footer={
          <>
            <button type="button" onClick={() => setPending(null)} className="rounded-lg px-3 py-2 text-sm font-semibold text-brand-muted hover:bg-brand-surface/60">Cancel</button>
            <button type="button" onClick={save} disabled={saving || !condition} className="rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save finding'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-brand-muted">Condition</label>
            <select value={condition} onChange={(e) => setCondition(e.target.value)} className="w-full rounded-md border border-brand-border bg-white px-2 py-1.5 text-sm">
              {conditions.map((c) => <option key={c} value={c}>{labelFor(c)}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-brand-muted">Tooth (FDI)</label>
            <select value={tooth} onChange={(e) => setTooth(e.target.value)} className="w-full rounded-md border border-brand-border bg-white px-2 py-1.5 text-sm">
              <option value="">— none / whole area —</option>
              {ALL_TEETH.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-brand-muted">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full rounded-md border border-brand-border bg-white px-2 py-1.5 text-sm" placeholder="e.g. distal caries, close to pulp" />
          </div>
          <label className="flex items-start gap-2 text-sm text-brand-ink">
            <input type="checkbox" checked={chartIt} onChange={(e) => setChartIt(e.target.checked)} className="mt-0.5 accent-brand-primary" disabled={!tooth || condition === 'healthy' || condition === 'missing'} />
            <span>
              Also add to the dental chart
              {suggestion && chartIt && tooth && (
                <span className="block text-[11px] text-brand-muted">Plans {suggestion.code} — {suggestion.description}</span>
              )}
              {(!tooth || condition === 'healthy' || condition === 'missing') && (
                <span className="block text-[11px] text-brand-muted">Pick a tooth and a chartable condition to enable.</span>
              )}
            </span>
          </label>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Remove finding"
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setConfirmDelete(null)} className="rounded-lg px-3 py-2 text-sm font-semibold text-brand-muted hover:bg-brand-surface/60">Keep</button>
            <button type="button" onClick={() => remove(confirmDelete.id)} className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:opacity-90">Remove</button>
          </>
        }
      >
        <p className="text-sm text-brand-ink">
          Remove this {confirmDelete ? labelFor(confirmDelete.condition) : ''} mark{confirmDelete?.tooth_number ? ` on tooth ${confirmDelete.tooth_number}` : ''}?
          {confirmDelete?.charted && ' The dental-chart entry it created stays in place.'}
        </p>
      </Modal>
    </DashboardLayout>
  )
}
